import { Box, ButtonBase, IconButton, makeStyles, Typography } from "@material-ui/core";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";

export const homeCardStyle = {
  backgroundColor: semanticColors["surface/card"],
  borderRadius: layoutTokens.radius.lg,
  boxShadow: `0px 2px 8px ${semanticColors["text/primary"]}0F`,
};

const useStyles = makeStyles({
  section: { minWidth: 0 },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: layoutTokens.space[8],
    minHeight: 26,
  },
  title: { ...textStyles["SP/Heading/H3"], color: semanticColors["text/primary"], minWidth: 0 },
  pcTitle: { ...textStyles["PC/Heading/H3"] },
  all: {
    ...textStyles["SP/Button/S"],
    color: semanticColors["text/link"],
    minHeight: 44,
    gap: layoutTokens.space[4],
    marginTop: -9,
    marginBottom: -9,
  },
  pcAll: { ...textStyles["PC/Button/M"] },
  arrow: { fontSize: 20 },
  subtitle: {
    ...textStyles["SP/Body/Small"],
    color: semanticColors["text/secondary"],
    marginTop: layoutTokens.space[8],
  },
  rail: {
    display: "flex",
    gap: layoutTokens.space[12],
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",
    paddingBottom: layoutTokens.space[4],
    "&::-webkit-scrollbar": { display: "none" },
    "& > *": { flexShrink: 0, scrollSnapAlign: "start" },
  },
  spRail: { marginRight: -layoutTokens.space[24], paddingRight: layoutTokens.space[24] },
  pcRail: { gap: layoutTokens.space[24] },
  railWrapper: { position: "relative" },
  arrows: {
    position: "absolute",
    top: "35%",
    left: layoutTokens.space[8],
    right: layoutTokens.space[8],
    display: "flex",
    justifyContent: "space-between",
    pointerEvents: "none",
  },
  arrowButton: {
    pointerEvents: "auto",
    "&:disabled": { visibility: "hidden" },
    width: 44,
    height: 44,
    padding: 0,
    border: `1px solid ${semanticColors["border/default"]}`,
    color: semanticColors["icon/strong"],
    backgroundColor: semanticColors["surface/card"],
  },
});

export const HomeSection: React.FC<{ title: string; subtitle?: string; onViewAll?: () => void }> = ({
  title,
  subtitle,
  onViewAll,
  children,
}) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  return (
    <section className={classes.section} aria-label={title}>
      <Box className={classes.header}>
        <Typography component="h2" className={`${classes.title} ${isMobile ? "" : classes.pcTitle}`}>
          {title}
        </Typography>
        {onViewAll && (
          <ButtonBase className={`${classes.all} ${isMobile ? "" : classes.pcAll}`} onClick={onViewAll}>
            すべて見る
            <ChevronRight className={classes.arrow} />
          </ButtonBase>
        )}
      </Box>
      {subtitle && <Typography className={classes.subtitle}>{subtitle}</Typography>}
      <Box mt={2}>{children}</Box>
    </section>
  );
};

/** SPはスワイプ、PCはスクロール位置に応じて左右のボタンを有効にする。 */
export const HomeRail: React.FC<{ label: string; arrows?: boolean }> = ({ label, arrows = true, children }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ previous: false, next: false });
  const updatePosition = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const previous = el.scrollLeft > 1;
    const next = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setPosition((current) => (current.previous === previous && current.next === next ? current : { previous, next }));
  }, []);
  useEffect(() => {
    let frame: number | null = null;
    // ResizeObserverの通知中に再描画せず、次のフレームに更新をまとめる。
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        updatePosition();
      });
    };
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleUpdate) : null;
    if (ref.current) observer?.observe(ref.current);
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [updatePosition]);
  useEffect(() => {
    updatePosition();
  }, [children, updatePosition]);
  const scroll = (direction: number) => {
    const el = ref.current;
    el?.scrollBy({ left: direction * el.clientWidth, behavior: "smooth" });
  };
  return (
    <div className={classes.railWrapper}>
      <div
        ref={ref}
        onScroll={updatePosition}
        className={`${classes.rail} ${isMobile ? classes.spRail : classes.pcRail}`}
        aria-label={label}
      >
        {children}
      </div>
      {!isMobile && arrows && (position.previous || position.next) && (
        <Box className={classes.arrows}>
          <IconButton
            className={classes.arrowButton}
            aria-label={`${label}を前へ`}
            disabled={!position.previous}
            onClick={() => scroll(-1)}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            className={classes.arrowButton}
            aria-label={`${label}を次へ`}
            disabled={!position.next}
            onClick={() => scroll(1)}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}
    </div>
  );
};
